import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@material-ui/core';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  SnapshotButton,
  RackTDsButton,
  ConnectRackButton,
  SetRackStateButton,
  DeleteRackButton
} from '.';

// Collapses the rack action buttons (Snapshot / TDs / Connect / Set State / Delete)
// into a single "Actions" dropdown. The parent decides when to render this menu
// (e.g. when the buttons row's measured width falls below a threshold — see
// RackDetails.js). The button instances below are kept mounted (visually hidden)
// and each one listens for an openSignal that this menu bumps when its MenuItem
// is clicked — so each action reuses its own modal logic without duplication.
export default function RackActionsMenu({ rack, isTDsButtonEnabled, jobInProgress }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [signals, setSignals] = useState({
    snapshot: 0,
    tds: 0,
    connect: 0,
    setState: 0,
    delete: 0
  });

  const showTDs = isTDsButtonEnabled && anyLRUsEnabled(rack);

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  // Bump the openSignal for the chosen action, then close the menu. The buttons
  // hidden below respond to the signal by opening their respective modals.
  const trigger = (key) => () => {
    setSignals((prev) => ({ ...prev, [key]: prev[key] + 1 }));
    closeMenu();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        variant="contained"
        onClick={openMenu}
        endIcon={<ArrowDropDownIcon />}
        fullWidth
        size="large"
      >
        Actions
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        PaperProps={{ sx: { width: '100%', maxWidth: '100%' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled={jobInProgress} onClick={trigger('snapshot')}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Snapshot" />
        </MenuItem>
        {showTDs && (
          <MenuItem disabled={jobInProgress} onClick={trigger('tds')}>
            <ListItemIcon>
              <FlightTakeoffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Aircraft Data" />
          </MenuItem>
        )}
        <MenuItem onClick={trigger('connect')}>
          <ListItemIcon>
            <TerminalOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Connect" />
        </MenuItem>
        <MenuItem disabled={jobInProgress} onClick={trigger('setState')}>
          <ListItemIcon>
            <PowerSettingsNewOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Power" />
        </MenuItem>
        <MenuItem disabled={jobInProgress} onClick={trigger('delete')}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/*
        Hidden button instances — kept mounted so their modals stay wired up.
        They react to the openSignal prop whenever a MenuItem is clicked.
        Using clip-path + aria-hidden instead of `display: none` so the buttons
        remain in the DOM (and their effects fire), but are not visible.
      */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap'
        }}
      >
        <SnapshotButton rackDetails={rack} disabled={jobInProgress} openSignal={signals.snapshot} />
        {showTDs && (
          <RackTDsButton rackDetails={rack} disabled={jobInProgress} openSignal={signals.tds} />
        )}
        <ConnectRackButton rackDetails={rack} openSignal={signals.connect} />
        <SetRackStateButton
          rackDetails={rack}
          disabled={jobInProgress}
          openSignal={signals.setState}
        />
        <DeleteRackButton
          rack={rack.name}
          region={rack.region}
          disabled={jobInProgress}
          openSignal={signals.delete}
        />
      </Box>
    </Box>
  );
}

function anyLRUsEnabled(rack) {
  if (rack.lrus) {
    return rack.lrus.length > 0;
  }
  return false;
}

RackActionsMenu.propTypes = {
  rack: PropTypes.object.isRequired,
  isTDsButtonEnabled: PropTypes.bool,
  jobInProgress: PropTypes.bool
};
